<script>
const DISPOSITIVOS = [
  {
    id: 'desktop',
    viewport: 1440,
    vars: {
      '--fs-12': '12px',
      '--fs-14': '14px',
      '--fs-16': '16px',
      '--fs-18': '18px',
      '--fs-20': '18px',
      '--fs-24': '20px',
      '--fs-30': '25px',
      '--lh-20': '18px',
      '--lh-24': '24px'
    }
  },
  {
    id: 'tablet',
    viewport: 1024,
    vars: {
      '--fs-12': '12px',
      '--fs-14': '14px',
      '--fs-16': '14px',
      '--fs-18': '17px',
      '--fs-20': '18px',
      '--fs-24': '20px',
      '--fs-30': '25px',
      '--lh-20': '18px',
      '--lh-24': '18px'
    }
  },
  {
    id: 'mobile',
    viewport: 375,
    vars: {
      '--fs-12': '12px',
      '--fs-14': '12px',
      '--fs-16': '10px',
      '--fs-18': '14px',
      '--fs-20': '14px',
      '--fs-24': '16px',
      '--fs-30': '25px',
      '--lh-20': '18px',
      '--lh-24': '18px'
    }
  }
]

const ESPACO_HORIZONTAL = 40

export default {
  name: 'ArticleBodyPreview',

  components: {
    Modal: () => import('@/components/general/Modal')
  },

  props: {
    active: {
      type: Boolean,
      default: false
    },

    body: {
      type: String,
      default: ''
    },

    article: {
      type: Object,
      default: () => ({})
    },

    themes: {
      type: Array,
      default: () => []
    }
  },

  data () {
    return {
      dispositivos: DISPOSITIVOS,
      dispositivo: 'desktop',
      larguraPalco: 0,
      alturaConteudo: 0
    }
  },

  computed: {
    dispositivoAtual () {
      return DISPOSITIVOS.find(item => item.id === this.dispositivo) || DISPOSITIVOS[0]
    },

    larguraViewport () {
      return this.dispositivoAtual.viewport
    },

    larguraColuna () {
      return Math.min(1200, this.larguraViewport * 0.9)
    },

    escala () {
      if (!this.larguraPalco) return 1

      return Math.min(1, this.larguraPalco / this.larguraViewport)
    },

    percentual () {
      return Math.round(this.escala * 100)
    },

    estiloMoldura () {
      return {
        width: (this.larguraViewport * this.escala) + 'px',
        height: (this.alturaConteudo * this.escala) + 'px'
      }
    },

    estiloColuna () {
      return Object.assign({
        width: this.larguraViewport + 'px',
        transform: 'scale(' + this.escala + ')'
      }, this.dispositivoAtual.vars)
    },

    temaNome () {
      const id = this.article.id_theme
      const achado = this.themes.find(t => String(t.id_theme || t.id || t.value) === String(id))

      return achado ? (achado.name || achado.text || achado.label) : null
    },

    dataFormatada () {
      const bruto = this.article.published_at || this.article.publishedAt

      if (!bruto) return null

      const d = new Date(String(bruto).replace(' ', 'T'))

      if (Number.isNaN(d.getTime())) return null

      return d.toLocaleDateString('pt-BR', {
        day: '2-digit',
        month: 'long',
        year: 'numeric'
      })
    },

    estiloCabecalho () {
      const capa = this.article.cover_image || this.article.coverImage

      if (typeof capa !== 'string' || capa === '') return null

      return {
        backgroundImage:
          'linear-gradient(135deg, rgba(77, 31, 155, 0.40) 0%, rgba(0, 92, 169, 0.40) 100%), ' +
          'url(' + capa + ')'
      }
    },

    urlPublica () {
      const base = process.env.VUE_APP_PORTAL_URL || ''

      return base.replace(/\/$/, '') + '/blog/' + (this.article.slug || '')
    },

    corpoVazio () {
      const semTags = (this.body || '').replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim()

      return semTags === ''
    }
  },

  watch: {
    active (aberto) {
      if (aberto) {
        this.$nextTick(this.medir)
      } else {
        this.desligarObservador()
      }
    },

    dispositivo () {
      this.$nextTick(this.medir)
    },

    body () {
      this.$nextTick(this.medir)
    }
  },

  mounted () {
    window.addEventListener('resize', this.medir)
  },

  beforeDestroy () {
    window.removeEventListener('resize', this.medir)
    this.desligarObservador()
  },

  methods: {
    fechar () {
      this.$emit('close')
    },

    medir () {
      const palco = this.$refs.palco
      const pagina = this.$refs.pagina

      if (!palco || !pagina) return

      this.larguraPalco = Math.max(0, palco.clientWidth - ESPACO_HORIZONTAL)
      this.alturaConteudo = pagina.offsetHeight
      this.ligarObservador(pagina)
    },

    ligarObservador (pagina) {
      if (typeof ResizeObserver === 'undefined' || this.observador) return

      this.observador = new ResizeObserver(() => {
        if (this.$refs.pagina) {
          this.alturaConteudo = this.$refs.pagina.offsetHeight
        }
      })

      this.observador.observe(pagina)
    },

    desligarObservador () {
      if (!this.observador) return

      this.observador.disconnect()
      this.observador = null
    }
  }
}
</script>

<template>
  <Modal
    class="article-body-preview"
    :active="active"
    :cancel="true"
    @close="fechar"
  >
    <div class="article-body-preview__cartao">
      <div class="article-body-preview__cabecalho">
        <h2 class="article-body-preview__titulo">
          {{ $t('articles.form:preview.title') }}
        </h2>
        <div class="article-body-preview__abas">
          <button
            v-for="item in dispositivos"
            :key="item.id"
            class="article-body-preview__aba"
            :class="{ 'is-active': dispositivo === item.id }"
            type="button"
            @click="dispositivo = item.id"
          >
            {{ $t('articles.form:preview.' + item.id) }}
          </button>
          <span class="article-body-preview__medida">
            {{ larguraViewport }} × {{ Math.round(larguraColuna) }}px · {{ percentual }}%
          </span>
        </div>
      </div>
      <p class="article-body-preview__aviso">
        {{ $t('articles.form:preview.aviso') }}
      </p>
      <div class="article-body-preview__navegador">
        <span class="article-body-preview__pontos">
          <i /><i /><i />
        </span>
        <span class="article-body-preview__url">{{ urlPublica }}</span>
      </div>
      <div
        ref="palco"
        class="article-body-preview__palco"
      >
        <div
          v-if="corpoVazio"
          class="article-body-preview__vazio"
        >
          {{ $t('articles.form:preview.vazio') }}
        </div>
        <div
          v-else
          class="article-body-preview__moldura"
          :style="estiloMoldura"
        >
          <div
            ref="pagina"
            class="article-body-preview__pagina"
            :class="'is-' + dispositivo"
            :style="estiloColuna"
          >
            <div
              class="article-body-preview__capa"
              :class="{ 'has-cover': !!estiloCabecalho }"
              :style="estiloCabecalho"
            >
              <div class="article-body-preview__capa-inner">
                <div class="article-body-preview__trilha">
                  Home <i>&rsaquo;</i> Blog <i>&rsaquo;</i>
                  <b>{{ article.title || $t('articles.form:preview.semTitulo') }}</b>
                </div>
                <div
                  v-if="temaNome"
                  class="article-body-preview__badge"
                >
                  <span>{{ temaNome }}</span>
                </div>
                <h1 class="article-body-preview__h1">
                  {{ article.title || $t('articles.form:preview.semTitulo') }}
                </h1>
                <p
                  v-if="article.subtitle"
                  class="article-body-preview__sub"
                >
                  {{ article.subtitle }}
                </p>
                <div class="article-body-preview__meta">
                  <span v-if="dataFormatada">{{ dataFormatada }}</span>
                  <span v-if="article.author">{{ article.author }}</span>
                  <span v-if="article.reading_time">
                    {{ article.reading_time }} min de leitura
                  </span>
                </div>
                <span class="article-body-preview__compartilhar">
                  {{ $t('articles.form:preview.compartilhar') }}
                </span>
              </div>
            </div>
            <div
              class="article-body-preview__coluna"
              v-html="body"
            />
          </div>
        </div>
      </div>
    </div>
  </Modal>
</template>

<style
  lang="scss"
  scoped
>
.article-body-preview {
  &__cartao {
    margin-top: 32px;
    padding: 24px;
    border-radius: 8px;
    background: #ffffff;
  }

  &__cabecalho {
    display: flex;
    align-items: center;
    justify-content: space-between;
    flex-wrap: wrap;
    gap: 12px;
  }

  &__titulo {
    margin: 0;
    font-size: 20px;
    font-weight: 700;
    color: #003c71;
  }

  &__abas {
    display: flex;
    align-items: center;
    gap: 8px;
  }

  &__aba {
    padding: 5px 14px;
    border: 1px solid #cccccc;
    border-radius: 4px;
    background: #f2f4f7;
    font-size: 13px;
    color: #364153;
    cursor: pointer;

    &.is-active {
      border-color: #005eb8;
      background: #005eb8;
      color: #ffffff;
    }
  }

  &__medida {
    font-size: 12px;
    color: #707070;
  }

  &__aviso {
    margin: 8px 0 16px;
    font-size: 12px;
    font-style: italic;
    color: #707070;
  }

  &__palco {
    max-height: 58vh;
    overflow: auto;
    padding: 20px;
    border: 1px solid #e0e0e0;
    border-radius: 0 0 6px 6px;
    background: #eef1f5;
  }

  &__vazio {
    padding: 32px;
    font-size: 14px;
    color: #707070;
    text-align: center;
  }

  &__moldura {
    position: relative;
    margin: 0 auto;
  }

  &__pagina {
    position: absolute;
    top: 0;
    left: 0;
    transform-origin: top left;
    background: #ffffff;
    box-shadow: 0 1px 6px rgba(0, 0, 0, 0.12);
    color: #364153;
  }

  &__capa {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: 16px;
    color: #ffffff;
    padding: 32px 0 40px;
    background: linear-gradient(135deg, #4d1f9b 0%, #005ca9 100%);

    &.has-cover {
      padding: 48px 0 64px;
      background-repeat: no-repeat;
      background-position: center;
      background-size: cover;
    }
  }

  &__capa-inner,
  &__coluna {
    width: 90%;
    max-width: 1200px;
    margin: 0 auto;
  }

  &__trilha {
    padding-bottom: 16px;
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-14);
    line-height: var(--lh-20);
    color: rgba(255, 255, 255, 0.8);

    i {
      font-style: normal;
    }

    b {
      font-weight: 400;
      color: #ffffff;
    }
  }

  &__badge {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 16px;
    padding: 8px 16px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.2);

    span {
      font-family: 'Lato', Helvetica, Arial, sans-serif;
      font-size: var(--fs-14);
      font-weight: 600;
      line-height: var(--lh-20);
      color: #ffffff;
      text-transform: uppercase;
    }
  }

  &__h1 {
    max-width: 798px;
    margin: 0 0 16px;
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: 36px;
    line-height: 45px;
    font-weight: 700;
    color: #ffffff;
  }

  &__sub {
    margin: 0 0 24px;
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-20);
    line-height: 30px;
    font-weight: 300;
    color: #ffffff;
  }

  &__meta {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    margin-bottom: 16px;
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-14);
    line-height: var(--lh-20);
    color: rgba(255, 255, 255, 0.9);
  }

  &__compartilhar {
    display: inline-flex;
    align-items: center;
    padding: 8px 20px;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.2);
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-14);
    font-weight: 600;
    line-height: var(--lh-20);
    color: #ffffff;
  }

  &__coluna {
    padding: 32px 0 40px;
  }

  &__navegador {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 12px;
    border: 1px solid #e0e0e0;
    border-bottom: 0;
    border-radius: 6px 6px 0 0;
    background: #f2f4f7;
  }

  &__pontos {
    display: flex;
    gap: 6px;

    i {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      background: #d3d8e0;
    }
  }

  &__url {
    overflow: hidden;
    font-size: 12px;
    color: #707070;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.article-body-preview__coluna ::v-deep {
  font-size: 16px;
  font-family: 'Lato', Helvetica, Arial, sans-serif;

  * {
    margin: 0;
  }

  h2 {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: 30px;
    line-height: 36px;
    font-weight: 700;
    color: #003c71;
  }

  h3 {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: 18px;
    line-height: 27px;
    font-weight: 700;
    color: #003c71;
  }

  h1,
  h4,
  h5,
  h6 {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-weight: 700;
    line-height: 1.125;
    color: inherit;
  }

  h1 {
    font-size: 2em;
  }

  h4 {
    font-size: 1em;
  }

  h5 {
    font-size: 0.83em;
  }

  h6 {
    font-size: 0.67em;
  }

  p {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-16);
    line-height: 26px;
    color: #364153;
  }

  ul,
  ol {
    list-style: none;
    padding-left: 0;
    display: flex;
    flex-direction: column;
    gap: 16px;
  }

  ol {
    counter-reset: blog-ol;
  }

  li {
    font-family: 'Lato', Helvetica, Arial, sans-serif;
    font-size: var(--fs-16);
    line-height: 26px;
    font-weight: 700;
    color: #364153;
    padding-left: 20px;
    position: relative;
  }

  ul li::before {
    content: '';
    position: absolute;
    left: 0;
    top: 8px;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: #005ca9;
  }

  ul li:nth-child(3n+2)::before {
    background: #4d1f9b;
  }

  ul li:nth-child(3n+3)::before {
    background: #00a859;
  }

  ol li {
    counter-increment: blog-ol;
    padding-left: 28px;
  }

  ol li::before {
    content: counter(blog-ol) '.';
    position: absolute;
    left: 0;
    top: 0;
    font-weight: 700;
    color: #005ca9;
  }

  ol li:nth-child(3n+2)::before {
    color: #4d1f9b;
  }

  ol li:nth-child(3n+3)::before {
    color: #00a859;
  }

  blockquote {
    background: #f9fafb;
    border-left: 4px solid #005ca9;
    border-radius: 0 8px 8px 0;
    padding: 24px 24px 24px 28px;
    margin: 24px 0;
    font-style: normal;
  }

  blockquote h3 {
    margin: 0 0 12px;
  }

  blockquote p {
    margin-bottom: 0;
  }

  a {
    color: #005eb8;
    text-decoration: underline;
  }

  img {
    max-width: 100%;
    height: auto;
    border-radius: 16px;
    margin: 16px 0;
  }

  strong {
    font-weight: 700;
  }

  em {
    font-style: italic;
  }

  .ql-align-center {
    text-align: center;
  }

  .ql-align-right {
    text-align: right;
  }

  .ql-align-justify {
    text-align: justify;
  }
}

.article-body-preview__pagina.is-mobile {
  .article-body-preview__capa {
    padding: 24px 0 32px;

    &.has-cover {
      padding: 24px 0 32px;
    }
  }

  .article-body-preview__h1 {
    font-size: 16px;
    line-height: 36px;
  }

  .article-body-preview__sub {
    font-size: var(--fs-16);
  }
}

.article-body-preview__pagina.is-mobile .article-body-preview__coluna ::v-deep {
  .ql-align-center img,
  .ql-align-right img,
  .ql-align-justify img {
    display: block;
    margin-inline: auto;
  }

  // A largura escolhida no editor vem como atributo `width="NN%"` no <img>.
  // No celular a porcentagem deixa de valer e a imagem ocupa a largura do
  // texto. Espelha a regra do portal em BlogArticlePage.vue, no @media de
  // 768px. Nao precisa de !important: o atributo `width` e dica de
  // apresentacao e perde para qualquer regra de autor.
  img {
    width: 100%;
  }
}
</style>
